import React, { useState } from 'react';
import { User } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import useCurrentUser from '../../hooks/useCurrentUser';
import useTeam from '../../hooks/useTeam';
import { emailDomain } from '../../utils/email';
import { DashboardSkeleton } from '../ui/LoadingStates';
import { useImageUpload } from './hooks/useImageUpload';
import ProfileHeader from './components/ProfileHeader';
import TabNavigation from './components/TabNavigation';
import OverviewTab from './components/OverviewTab';
import TeamTab from './components/TeamTab';
import TeamMembersTab from './components/TeamMembersTab';
import ProfileTab from './components/ProfileTab';
import MediaTab from './components/MediaTab';
import ProjectsTab from './components/ProjectsTab';
import ClientsTab from './components/ClientsTab';
import Modals from './components/Modals';

export default function EntrepreneurDashboard({ clientData, isReadOnly = false }) {
  const { user, loading } = useCurrentUser();
  const domain = emailDomain(user?.email || '');

  const displayUser = clientData || user;
  const displayLoading = isReadOnly ? false : loading;

  // Use image upload hook
  const {
    profilePicture,
    bannerPhoto,
    organizationLogo,
    setProfilePicture,
    setBannerPhoto,
    setOrganizationLogo,
    uploadProfilePicture,
    uploadBannerPhoto,
    uploadOrganizationLogo,
  } = useImageUpload(displayUser?.uid, 'clients');

  // Initialize images from displayUser
  React.useEffect(() => {
    if (displayUser) {
      setProfilePicture(displayUser.profilePicture || null);
      setBannerPhoto(displayUser.bannerPhoto || null);
      setOrganizationLogo(displayUser.organizationLogo || null);
    }
  }, [displayUser, setProfilePicture, setBannerPhoto, setOrganizationLogo]);

  const {
    team,
    addMember,
    removeMember,
    addAllowedDomain,
    removeAllowedDomain,
    toggleDomainRestriction,
    loading: teamLoading,
    error: teamError,
  } = useTeam({ user: isReadOnly ? displayUser : user });

  // In read-only mode, we might not have team data, so we'll create a mock team object
  const displayTeam =
    isReadOnly && !team
      ? {
          members: [],
          organization: displayUser.organization,
          allowedDomains: [],
          allowAnyDomain: false,
        }
      : team;

  // State for modals and tabs
  const [openAdd, setOpenAdd] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [showMediaCarousel, setShowMediaCarousel] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);

  React.useEffect(() => {
    if (teamError) toast.error(teamError);
  }, [teamError]);

  if (displayLoading) {
    return <DashboardSkeleton />;
  }

  if (!displayUser) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <User className="h-8 w-8 text-red-500" />
          </div>
          <p className="text-xl text-red-500">
            {isReadOnly ? 'User profile not found' : 'Please log in to access your dashboard.'}
          </p>
        </div>
      </motion.div>
    );
  }

  const isClient = displayUser.role === 'client';
  const hasClientData = displayUser.firstname || displayUser.lastname || displayUser.email;

  const settings = {
    name: displayUser.firstname || '',
    surname: displayUser.lastname || '',
    email: displayUser.email || '',
    phone: displayUser.phone || '',
    location: displayUser.location || '',
    organization: displayUser.organization || '',
    role: displayUser.businessStage || '',
    profileLink: displayUser.profileLink || '',
    country: displayUser.country || '',
    program: displayUser.program || '',
    status: displayUser.status || '',
  };

  const handleMediaUploadComplete = uploadedMedia => {
    setShowMediaUpload(false);
    toast.success(`${uploadedMedia.length} media file(s) uploaded successfully`);
  };

  const handleClientAdded = newClient => {
    setShowClientForm(false);
    toast.success('Client added successfully');
  };

  const handleProjectAdded = newProject => {
    setShowProjectForm(false);
    toast.success('Project added successfully');
  };

  const openMediaCarousel = index => {
    setSelectedMediaIndex(index);
    setShowMediaCarousel(true);
  };

  const renderTabContent = () => {
    const commonProps = {
      displayUser,
      isReadOnly,
    };

    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab {...commonProps} displayTeam={displayTeam} hasClientData={hasClientData} />
        );

      case 'team':
        return (
          <TeamTab
            {...commonProps}
            displayTeam={displayTeam}
            teamLoading={teamLoading}
            onAddMember={() => setOpenAdd(true)}
            onRemoveMember={async m => {
              try {
                await removeMember(m);
                toast.success('Member removed');
              } catch (e) {
                toast.error(e?.message || 'Failed to remove');
              }
            }}
            onOpenSettings={() => setOpenSettings(true)}
          />
        );

      case 'team-members':
        return (
          <TeamMembersTab
            {...commonProps}
            showAddModal={showAddModal}
            onExternalAddModalClose={() => setShowAddModal(false)}
          />
        );

      case 'profile':
        return <ProfileTab {...commonProps} />;

      case 'media':
        return <MediaTab {...commonProps} onUploadMedia={() => setShowMediaUpload(true)} />;

      case 'projects':
        return <ProjectsTab {...commonProps} onAddProject={() => setShowProjectForm(true)} />;

      case 'clients':
        return <ClientsTab {...commonProps} onAddClient={() => setShowClientForm(true)} />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        className="mt-16"
      />

      <ProfileHeader
        name={`${settings.name} ${settings.surname}`}
        role={settings.role}
        organization={settings.organization}
        location={settings.location}
        email={settings.email}
        phone={settings.phone}
        profilePicture={profilePicture}
        bannerPhoto={bannerPhoto}
        organizationLogo={organizationLogo}
        onProfilePictureUpload={uploadProfilePicture}
        onBannerUpload={uploadBannerPhoto}
        onOrganizationLogoUpload={uploadOrganizationLogo}
        isReadOnly={isReadOnly}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mx-auto max-w-7xl px-6 py-12"
      >
        {/* Navigation Tabs */}
        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Tab Content */}
        {renderTabContent()}

        {/* Modals */}
        <Modals
          isReadOnly={isReadOnly}
          openAdd={openAdd}
          setOpenAdd={setOpenAdd}
          openSettings={openSettings}
          setOpenSettings={setOpenSettings}
          showMediaUpload={showMediaUpload}
          setShowMediaUpload={setShowMediaUpload}
          showClientForm={showClientForm}
          setShowClientForm={setShowClientForm}
          showProjectForm={showProjectForm}
          setShowProjectForm={setShowProjectForm}
          displayUser={displayUser}
          displayTeam={displayTeam}
          user={user}
          addMember={addMember}
          removeMember={removeMember}
          addAllowedDomain={addAllowedDomain}
          removeAllowedDomain={removeAllowedDomain}
          toggleDomainRestriction={toggleDomainRestriction}
          handleMediaUploadComplete={handleMediaUploadComplete}
          handleClientAdded={handleClientAdded}
          handleProjectAdded={handleProjectAdded}
        />
      </motion.div>
    </div>
  );
}
